import { Injectable } from '@angular/core';
import { catchError, Observable, of, switchMap, take } from 'rxjs';

import { UserManagementService } from '@app/api/api-services/user-management.service';
import { SystemEvent } from '@app/shared/enums';
import { UserInfo } from '@app/shared/models';
import { EventsService, TokenStorageService } from '@app/shared/services';
import { CustomUtils } from '@app/shared/utils/custom-utils';
import { GainsightUser, GainsightAccount } from './gainsight.model';
import { ConfigService } from '@app/configuration/config.service';

declare const aptrinsic: (method: string, user: GainsightUser, account: GainsightAccount) => void;

@Injectable()
export class GainsightService {
  private scriptLoaded = false;
  private initQueued: UserInfo;

  constructor(
    private eventsService: EventsService,
    private userManagement: UserManagementService,
    private readonly tokenStorage: TokenStorageService,
    private readonly configService: ConfigService
  ) {
    this.eventsService.subscribe(SystemEvent.GainsightScriptLoaded, () => {
      this.scriptLoaded = true;

      this.reinitQueuedGainSight();
    });
  }

  private queueInitGainSight(userInfo: UserInfo) {
    this.initQueued = userInfo;
  }

  private reinitQueuedGainSight() {
    if (this.initQueued) {
      this.initGainSight(this.initQueued);

      this.initQueued = undefined;
    }
  }

  private getGainSightUserAndAccount(
    userInfo: UserInfo
  ): Observable<{ user: GainsightUser; account: GainsightAccount }> {
    const userRole = CustomUtils.getUserRole(userInfo);
    const email = userInfo.IsSiteAdmin === true ? userInfo.Email : null;
    const userId = userInfo.GlobalUserId ?? userInfo.UserId;

    const config = this.configService.getConfig();
    const siteId =
      config.gainSightEnviromentPrefix === 'LIVE'
        ? 'FTO | ' + userInfo.CurrentSiteId.toString()
        : 'FTO | ' + config.gainSightEnviromentPrefix + ' | ' + userInfo.CurrentSiteId.toString();

    let adminAuthorityTimestamp: number | null = null;
    if (userInfo.SiteAdminAuthorityDate) {
      const parsableDateString = userInfo.SiteAdminAuthorityDate.replace(' ', 'T');
      const date = new Date(parsableDateString);
      if (!isNaN(date.getTime())) {
        adminAuthorityTimestamp = date.getTime() / 1000;
      }
    }

    const user: GainsightUser = {
      id: 'FTO | ' + userInfo.CurrentSiteId + ' | ' + userId,
      ftoSiteId: siteId,
      firstName: userInfo.UserFirstname,
      email: email,
      ftoSignUp: '',
      ftoUserRole: userRole,
      ftoProgramType:
        userInfo.CurrentProgram === undefined ? '' : CustomUtils.getProgramType(userInfo.CurrentProgram.Type),
      ftoProgramName: userInfo.CurrentProgramName,
      globalUserId: userInfo.GlobalUserId,
      globalSiteId: userInfo.CurrentSite.GlobalSiteId,
      FTO_AdminAuthority_Date: adminAuthorityTimestamp
    };
    const account: GainsightAccount = {
      id: siteId,
      name: userInfo.CurrentSite.Name,
      sfdcId: userInfo.SalesForceId
    };

    if (this.tokenStorage.signedUpDate) {
      user.ftoSignUp = this.tokenStorage.signedUpDate; //add year

      return of({ user, account });
    } else {
      return this.userManagement.getSignUpDate().pipe(
        switchMap(({ Value }) => {
          user.ftoSignUp = Value;
          this.tokenStorage.signedUpDate = Value;

          return of({ user, account });
        }),
        catchError(() => {
          return of({ user, account });
        })
      );
    }
  }

  initGainSight(userInfo: UserInfo) {
    if (this.configService.getConfig()?.gainSightPXCode) {
      if (this.scriptLoaded) {
        this.getGainSightUserAndAccount(userInfo)
          .pipe(take(1))
          .subscribe(({ user, account }) => {
            aptrinsic('identify', user, account);
          });
      } else {
        this.queueInitGainSight(userInfo);
      }
    }
  }
}
